import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isFreeSku } from '../../data/product/product-lookup.data';
import { AnalyticsService } from '../analytics/analytics.service';

export interface CartItem {
  sku: string;
  /** copies of the same download (a gift, a second licence); 1..MAX_QUANTITY */
  quantity: number;
  addedAt: number;
}

/** the key keeps its old name so carts from before the rename (basket) survive it */
const STORAGE_KEY = 'codigrate.basket.v1';
/** different lines; well under Stripe Checkout's 100 line items, and more than the whole catalogue for a while (owner, 2026-09-20: 20 was too few once two platforms sold) */
export const MAX_ITEMS = 50;
export const MAX_QUANTITY = 10;

/**
 * what an Add to cart did: a new line, one more copy of a line already there,
 * or nothing: 'full' = the line already has MAX_QUANTITY copies,
 * 'cart-full' = the cart already has MAX_ITEMS different lines,
 * 'already' = a free listing that is in the cart already (free lines are one copy).
 */
export type CartAddResult = 'added' | 'increased' | 'full' | 'cart-full' | 'already' | 'invalid';

/**
 * The cart's order: oldest line first, but the same theme's lines next to
 * each other (owner, 2026-09-20: London for Sublime Text over London for
 * macOS Terminal, never apart), each group where its first line was added.
 * The theme is the sku's first segment ("london/sublime-text").
 */
export function groupByTheme( items: CartItem[] ): CartItem[] {
  const groups = new Map<string, CartItem[]>();
  for ( const item of [ ...items ].sort( ( a, b ) => a.addedAt - b.addedAt ) ) {
    const theme = item.sku.split( '/' )[ 0 ];
    const group = groups.get( theme );
    if ( group ) group.push( item );
    else groups.set( theme, [ item ] );
  }
  // the paid lines first, the free ones after them (owner, 2026-09-21); a theme is either sold or
  // free on every platform, so a group is one or the other
  const all = Array.from( groups.values() );
  const paid = all.filter( group => !isFreeSku( group[ 0 ].sku ) );
  const free = all.filter( group => isFreeSku( group[ 0 ].sku ) );
  return paid.concat( free ).reduce( ( flat, group ) => flat.concat( group ), [] as CartItem[] );
}

/**
 * The cart: store SKUs with a quantity each, kept in localStorage (no
 * accounts). Other tabs stay in sync through the `storage` event.
 */
@Injectable( {
  providedIn: 'root',
} )
export class CartService {

  private readonly itemsSubject = new BehaviorSubject<CartItem[]>( this.read() );

  /** the cart, the paid lines first, oldest first, the same theme's lines together (see groupByTheme). */
  readonly items$: Observable<CartItem[]> = this.itemsSubject.asObservable();
  /** the badge number: every copy counts. */
  readonly count$: Observable<number> = this.items$.pipe( map( items => items.reduce( ( n, item ) => n + item.quantity, 0 ) ) );

  constructor( private analyticsService: AnalyticsService ) {
    if ( typeof window !== 'undefined' ) {
      window.addEventListener( 'storage', ( event ) => {
        if ( event.key === STORAGE_KEY ) this.itemsSubject.next( this.read() );
      } );
    }
  }

  get items(): CartItem[] {
    return this.itemsSubject.value;
  }

  get skus(): string[] {
    return this.items.map( item => item.sku );
  }

  get count(): number {
    return this.items.reduce( ( n, item ) => n + item.quantity, 0 );
  }

  has( sku: string ): boolean {
    return this.items.some( item => item.sku === sku );
  }

  /**
   * The cart as event properties, on every cart event (owner ask 2026-09-20:
   * "when a user adds an item, log what is in the cart too"): the copies, the
   * lines, the skus, and the lines with their copies ("ocean/sublime-text ×2").
   */
  analyticsProps(): { cartSize: number; cartLines: number; cartSkus: string[]; cartItems: string[] } {
    return {
      cartSize: this.count,
      cartLines: this.items.length,
      cartSkus: this.skus,
      cartItems: this.items.map( item => ( item.quantity > 1 ? `${ item.sku } ×${ item.quantity }` : item.sku ) ),
    };
  }

  quantityOf( sku: string ): number {
    return this.items.find( item => item.sku === sku )?.quantity ?? 0;
  }

  /**
   * Add to cart: a new line with one copy, or one more copy of a line already
   * there (a second click is a second copy, like any store; copies are also
   * adjusted on the cart page). Says what happened so the caller can show it.
   */
  add( sku: string, source?: string ): CartAddResult {
    if ( !sku ) return 'invalid';
    const current = this.quantityOf( sku );
    if ( current > 0 ) {
      // a free listing is one copy (2026-09-21): a second copy would be the same file for nothing
      if ( isFreeSku( sku ) ) return 'already';
      if ( current >= MAX_QUANTITY ) return 'full';
      this.write( this.items.map( item => ( item.sku === sku ? { ...item, quantity: current + 1 } : item ) ) );
      this.analyticsService.logEvent( 'Cart Item Added', { sku, source: source || 'unknown', quantity: current + 1, ...this.analyticsProps(), ...this.analyticsService.pageContext() } );
      return 'increased';
    }
    if ( this.items.length >= MAX_ITEMS ) return 'cart-full';
    this.write( [ ...this.items, { sku, quantity: 1, addedAt: Date.now() } ] );
    // the page context says where the add happened and what led there (a blog post, a tool...)
    this.analyticsService.logEvent( 'Cart Item Added', { sku, source: source || 'unknown', quantity: 1, ...this.analyticsProps(), ...this.analyticsService.pageContext() } );
    return 'added';
  }

  /** the quantity stepper; 0 removes the line. */
  setQuantity( sku: string, quantity: number, source?: string ): void {
    const next = Math.floor( Number( quantity ) );
    if ( !this.has( sku ) || !Number.isFinite( next ) ) return;
    if ( next <= 0 ) {
      this.remove( sku, source );
      return;
    }
    const clamped = isFreeSku( sku ) ? 1 : Math.min( MAX_QUANTITY, next );
    this.write( this.items.map( item => ( item.sku === sku ? { ...item, quantity: clamped } : item ) ) );
    this.analyticsService.logEvent( 'Cart Quantity Changed', { sku, quantity: clamped, source: source || 'cart', ...this.analyticsProps() } );
  }

  remove( sku: string, source?: string ): void {
    if ( !this.has( sku ) ) return;
    this.write( this.items.filter( item => item.sku !== sku ) );
    this.analyticsService.logEvent( 'Cart Item Removed', { sku, source: source || 'cart', ...this.analyticsProps() } );
  }

  /** puts a removed line back (the undo toast), with its quantity, at its old position when possible. */
  restore( item: CartItem ): void {
    if ( !item?.sku || this.has( item.sku ) ) return;
    const next = [ ...this.items, item ].sort( ( a, b ) => a.addedAt - b.addedAt );
    this.write( next );
  }

  clear(): void {
    this.write( [] );
  }

  private read(): CartItem[] {
    try {
      const raw = localStorage.getItem( STORAGE_KEY );
      if ( !raw ) return [];
      const parsed = JSON.parse( raw );
      const items: any[] = Array.isArray( parsed?.items ) ? parsed.items : [];
      return groupByTheme( items
        .filter( item => item && typeof item.sku === 'string' && /^[a-z0-9-]+\/[a-z0-9-]+$/.test( item.sku ) )
        .map( item => ( {
          sku: item.sku,
          quantity: isFreeSku( item.sku ) ? 1 : Math.min( MAX_QUANTITY, Math.max( 1, Math.floor( Number( item.quantity ) ) || 1 ) ),
          addedAt: Number( item.addedAt ) || Date.now(),
        } ) )
        .slice( 0, MAX_ITEMS ) );
    } catch {
      return [];
    }
  }

  private write( lines: CartItem[] ): void {
    const items = groupByTheme( lines );
    try {
      localStorage.setItem( STORAGE_KEY, JSON.stringify( { items } ) );
    } catch {
      // private mode / quota: the in-memory cart still works for this page
    }
    this.itemsSubject.next( items );
  }
}
